/**
 * Tarea B (fc-affect-adaptive-tuning) — heurística de "frase colgada" portada
 * 1:1 desde el backend: coordinator/question.py::_looks_incomplete_transcript
 * (ms-py-full-conversation-agent). Mismo propósito ahí y acá: distinguir un
 * transcript que terminó una idea de uno cortado a mitad de frase (logs16 —
 * Gemini cerró el turno del candidato en una pausa a mitad de idea).
 *
 * Pura y determinística — mismo texto, mismo resultado. Sin esto el front NO
 * tiene forma de saber, a partir de un interim de Web Speech, si el candidato
 * "terminó de hablar" o solo hizo una pausa a mitad de frase (el RMS por sí
 * solo no distingue eso — ver fc-session.component.ts handleLocalAsrTranscript).
 *
 * OJO al tocar esto: era un espejo fiel del lado Python. Desde la regla 4 nueva
 * (ver looksIncompleteTranscript) el espejo diverge A PROPÓSITO en el caso "sin
 * puntuación terminal"; el lado Python (si existe) debe portarse por separado.
 * El BE de low-api usa su propio `looksDanglingTranscript` (estricto).
 */

/** Los mismos 4 signos de cierre de frase que el lado Python (`.!?…`). */
const TERMINAL_PUNCT: ReadonlySet<string> = new Set(['.', '!', '?', '…']);

/** Base espejo de `dangling` en question.py (+ muletillas/hesitación y conectores extra). */
const DANGLING_WORDS: ReadonlySet<string> = new Set([
  'o', 'y', 'de', 'que', 'el', 'la', 'un', 'una', 'con', 'para', 'por',
  'en', 'a', 'al', 'del', 'los', 'las', 'se', 'me', 'te', 'pero',
  'porque', 'como', 'si', 'cuando', 'entonces', 'este', 'esta', 'eso',
  'esa', 'su', 'mi', 'tu', 'le', 'lo', 'nos', 'les', 'está', 'estoy',
  'estan', 'están', 'era', 'fue', 'son', 'es', 'hay',
  // Fillers / hesitación — logs7 mid-thought "em a las", "eh", "mm".
  'eh', 'em', 'mm', 'mmm', 'hmm', 'ahm', 'eee', 'pues',
  'osea', 'o sea', 'tipo', 'digamos', 'bueno',
  // Conectores/preposiciones extra: al sacar la regla "sin punto ⇒ colgado"
  // ("Bueno, en un escenario donde" dejó de caer por catch-all).
  'donde', 'aunque', 'mientras', 'sobre', 'entre', 'desde', 'hasta', 'sin',
  'ni', 'e', 'u', 'cual', 'quien',
]);

/** Espejo exacto del segundo set (`{"eh", "em", "mm", "mmm", "hmm", "ahm"}`) —
 *  NO es el mismo set que DANGLING_WORDS (ese incluye más palabras). */
const FILLER_TAIL_WORDS: ReadonlySet<string> = new Set(['eh', 'em', 'mm', 'mmm', 'hmm', 'ahm']);

/** Equivalente a Python `.strip(".,;:¡!¿?'\"")` — quita esos signos de ambos bordes. */
function stripEdgePunctFull(word: string): string {
  return word.replace(/^[.,;:¡!¿?'"]+/, '').replace(/[.,;:¡!¿?'"]+$/, '');
}

/** Equivalente a Python `.strip(".,;:")` — set más chico, usado solo en el chequeo de cola. */
function stripEdgePunctBasic(word: string): string {
  return word.replace(/^[.,;:]+/, '').replace(/[.,;:]+$/, '');
}

/**
 * Respuestas cortas COMPLETAS: "sí", "no sé"... cierran idea aunque tengan < 3
 * palabras; sin esta excepción esperaban base × EOS_INCOMPLETE_FACTOR (1.5).
 * Se compara el texto entero normalizado (sin tildes ni signos).
 */
const COMPLETE_SHORT_ANSWERS: ReadonlySet<string> = new Set([
  'si', 'no', 'no se', 'claro', 'correcto', 'exacto', 'listo',
]);

function normalizeShortAnswer(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Muletillas de arranque ("Bueno este en este caso..."): no cuentan como
 * contenido. Tras quitarlas, una frase con < MIN_CONTENT_WORDS_AFTER_FILLERS
 * palabras de contenido sigue siendo un arranque, no una idea cerrada.
 */
const LEADING_FILLERS: ReadonlySet<string> = new Set([
  'bueno', 'pues', 'digamos', 'eh', 'em', 'mmm',
]);
/** "este" es muletilla SOLO si sigue a otra muletilla ("bueno este…", "eh este…"):
 *  al arranque es demostrativo ("Este proyecto usa Kafka" = frase completa). */
const FILLER_AFTER_FILLER = 'este';
/** Cabeza afirmativa/negativa tras una muletilla ("bueno sí lo usé", "pues claro"):
 *  cierra idea aunque el resto sea corto. */
const AFFIRMATIVE_HEADS: ReadonlySet<string> = new Set([
  'si', 'no', 'claro', 'correcto', 'exacto', 'listo', 'obvio', 'seguro',
]);
const MIN_CONTENT_WORDS_AFTER_FILLERS = 4;

/** Cantidad de muletillas iniciales (soporta "o sea" de dos palabras). */
function countLeadingFillers(words: string[]): number {
  let i = 0;
  while (i < words.length) {
    const w = stripEdgePunctFull(words[i].toLowerCase());
    if (LEADING_FILLERS.has(w) || (i > 0 && w === FILLER_AFTER_FILLER)) {
      i += 1;
    } else if (w === 'o' && stripEdgePunctFull((words[i + 1] || '').toLowerCase()) === 'sea') {
      i += 2;
    } else {
      break;
    }
  }
  return i;
}

/** Mínimo de palabras para considerar que un final trae una idea (< 3 = chunk del ASR). */
const MIN_WORDS_FOR_COMPLETE = 3;

/**
 * True si `text` "quedó colgado" a mitad de idea.
 *   1. Vacío → false.
 *   2. Termina en signo de cierre (`.!?…`) → false (frase cerrada).
 *   3. Última palabra colgante / muletilla de cola → true.
 *   4. Menos de 3 palabras → true (chunk suelto del ASR: "bueno", "sí claro"),
 *      salvo respuestas cortas completas (sí, no, no sé, claro, correcto, exacto, listo).
 *   5. Tras quitar muletillas iniciales (bueno, pues, o sea, digamos, eh,
 *      em, mmm; "este" solo tras otra muletilla), salvo cabeza afirmativa
 *      ("bueno sí lo usé", "pues claro"), < 4 palabras de contenido → true ("Bueno este en este caso").
 *   6. Cualquier otro caso → false.
 *
 * DIVERGENCIA DELIBERADA vs el backend (`_looks_incomplete_transcript`): la
 * regla vieja "sin puntuación terminal ⇒ incompleto" se eliminó. El ASR del
 * browser (Chrome es-419) NO puntúa nunca, así que esa regla marcaba TODO final
 * como colgado y el silencio adaptativo (turn_silence_s por perfil de ritmo)
 * jamás aplicaba: se esperaba siempre el piso largo (~7 s de cierre). Ahora
 * "colgado" = termina en conector/muletilla o es demasiado corto.
 */
export function looksIncompleteTranscript(text: string): boolean {
  const t = (text || '').trim();
  if (!t) return false;
  const lastChar = t[t.length - 1];
  if (TERMINAL_PUNCT.has(lastChar)) return false;

  const words = t.split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;

  // Antes del chequeo de colgantes: "no se" (sin tilde) termina en "se".
  if (words.length < MIN_WORDS_FOR_COMPLETE && COMPLETE_SHORT_ANSWERS.has(normalizeShortAnswer(t))) {
    return false;
  }

  const last = stripEdgePunctFull(words[words.length - 1].toLowerCase());
  if (DANGLING_WORDS.has(last)) return true;
  // "o sea" va partido en dos palabras: el set de arriba solo ve la última.
  if (last === 'sea' && stripEdgePunctFull((words[words.length - 2] || '').toLowerCase()) === 'o') {
    return true;
  }

  const tail = words.slice(-3);
  if (tail.some((w) => FILLER_TAIL_WORDS.has(stripEdgePunctBasic(w.toLowerCase())))) return true;

  // Muletillas iniciales: no cuentan. Solo aplica el umbral de 4 cuando había
  // alguna (sin ellas, el umbral sigue siendo MIN_WORDS_FOR_COMPLETE).
  const fillers = countLeadingFillers(words);
  if (fillers > 0) {
    const content = words.slice(fillers);
    if (content.length < MIN_CONTENT_WORDS_AFTER_FILLERS) {
      if (content.length > 0 && AFFIRMATIVE_HEADS.has(normalizeShortAnswer(content[0]))) {
        return false; // "bueno sí lo usé" / "pues claro": respuesta afirmativa completa
      }
      if (content.length > 0 && content.length < MIN_WORDS_FOR_COMPLETE
          && COMPLETE_SHORT_ANSWERS.has(normalizeShortAnswer(content.join(' ')))) {
        return false; // "bueno no sé" sigue siendo una respuesta corta completa
      }
      return true;
    }
  }

  return words.length < MIN_WORDS_FOR_COMPLETE;
}
